// lib/azure-client.ts
import { getExtensionFromMime, storageAccountName } from '#lib/azure-shared';

const tempFileCache = new Map<string, { url: string; timestamp: number }>();
const TWENTY_FOUR_HOURS_IN_MS = 24 * 60 * 60 * 1000;

const randomId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2);
};

async function getFileHash(file: Blob): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export const getCloudTmpUrl = async (
    bodyOrUrl: File | Blob | string,
    expireTime: number = 60 * 60 * 2,
    contentType?: string,
    signal?: AbortSignal
): Promise<string> => {
    let blobToUpload: Blob;
    let finalContentType: string | undefined = contentType;
    let filenameHint: string | undefined;

    const generateRandomFilenameExtension = (extension?: string) => {
        const ext = extension ? `.${extension}`.toLowerCase() : '.tmp';
        return `${randomId()}${ext}`;
    };

    if (typeof bodyOrUrl === 'string') {
        try {
            const response = await fetch(bodyOrUrl, { signal });
            if (!response.ok) {
                throw new Error(`Failed to fetch resource from URL: ${response.status} ${response.statusText}`);
            }
            const fetchedContentType = response.headers.get('content-type');
            finalContentType = contentType || fetchedContentType || 'application/octet-stream';
            blobToUpload = await response.blob();
            filenameHint = generateRandomFilenameExtension(getExtensionFromMime(finalContentType));
        } catch (error) {
            console.error('Azure: Error fetching or processing URL:', error);
            throw error;
        }
    } else {
        blobToUpload = bodyOrUrl;
        finalContentType = contentType || blobToUpload.type || 'application/octet-stream';
        const originalFilename = bodyOrUrl instanceof File ? bodyOrUrl.name : undefined;
        const extension = getExtensionFromMime(finalContentType) || originalFilename?.split('.').pop();
        filenameHint = generateRandomFilenameExtension(extension);
    }

    const signature = await getFileHash(blobToUpload);
    const cached = tempFileCache.get(signature);
    const now = Date.now();
    if (cached && now - cached.timestamp < TWENTY_FOUR_HOURS_IN_MS) {
        return cached.url;
    }

    const sasResponse = await fetch('/api/get-azure-upload-sas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            contentType: finalContentType,
            expireTimeMinutes: Math.ceil(expireTime / 60),
            filenameHint,
        }),
        signal,
    });

    if (!sasResponse.ok) {
        const errorData = await sasResponse.text();
        console.error('Azure: Error getting SAS URL:', sasResponse.status, errorData);
        throw new Error(`Failed to get SAS URL: ${sasResponse.statusText} - ${errorData}`);
    }

    const { sasUrl, blobName } = await sasResponse.json();
    if (!sasUrl || !blobName) {
        throw new Error('Azure: Backend did not return a valid SAS URL or blob name.');
    }

    const uploadResponse = await fetch(sasUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': finalContentType,
            'x-ms-blob-type': 'BlockBlob',
        },
        body: blobToUpload,
        signal,
    });

    if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Azure: Direct upload using SAS URL failed:', uploadResponse.status, uploadResponse.statusText, errorText);
        throw new Error(`Azure SAS upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`);
    }

    const publicUrl = `https://${storageAccountName}.blob.core.windows.net/temp-public/${blobName}`;
    tempFileCache.set(signature, { url: publicUrl, timestamp: now });
    return publicUrl;
};

/** A PUT of a blob to its SAS URL that reports how much has gone (fetch cannot report an upload's progress). */
const putWithProgress = (url: string, blob: Blob, contentType: string, onProgress: (fraction: number) => void, signal?: AbortSignal) =>
    new Promise<{ ok: boolean; status: number; statusText: string; text: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
        xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, statusText: xhr.statusText, text: xhr.responseText });
        xhr.onerror = () => reject(new Error('Azure: the upload failed on the network.'));
        xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
        signal?.addEventListener('abort', () => xhr.abort(), { once: true });
        xhr.send(blob);
    });

export const uploadFileToCloud = async (
    blob: Blob,
    filename: string,
    contentType: string,
    signal?: AbortSignal,
    ownerId?: string | null,
    /** Called with the share uploaded (0 to 1), for a caller that shows it. */
    onProgress?: (fraction: number) => void
): Promise<string | null> => {
    try {
        const sasResponse = await fetch('/api/assets/upload-sas', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename, contentType, ownerId }),
            signal,
        });

        if (!sasResponse.ok) {
            const errorData = await sasResponse.text();
            console.error('Azure: Error getting asset upload SAS URL:', sasResponse.status, errorData);
            throw new Error(`Failed to get asset upload SAS URL: ${sasResponse.statusText} - ${errorData}`);
        }

        const { sasUrl, assetPath } = await sasResponse.json();
        if (!sasUrl || !assetPath) {
            throw new Error('Azure: Backend did not return a valid SAS URL or asset path for upload.');
        }

        const uploadResponse = onProgress
            ? await putWithProgress(sasUrl, blob, contentType, onProgress, signal)
            : await fetch(sasUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': contentType,
                    'x-ms-blob-type': 'BlockBlob',
                },
                body: blob,
                signal,
            });

        if (!uploadResponse.ok) {
            const errorText = 'text' in uploadResponse && typeof uploadResponse.text === 'string' ? uploadResponse.text : await (uploadResponse as Response).text();
            console.error('Azure: Direct asset upload failed:', uploadResponse.status, uploadResponse.statusText, errorText);
            throw new Error(`Azure: Direct asset upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`);
        }

        return assetPath;
    } catch (error) {
        console.error('Azure: Error during asset SAS upload process:', error);
        return null;
    }
};

export const uploadImageDataToCloud = async (
    imageData: ImageData,
    imageFormat: 'webp' | 'png' | 'jpeg' = 'png',
    signal?: AbortSignal,
    ownerId?: string | null,
    imageQuality?: number
): Promise<string | null> => {
    if (typeof document === 'undefined') {
        console.error("Azure: uploadImageDataToCloud can only run in a browser environment with 'document'.");
        return null;
    }

    let mimeType: string;
    let fileExtension: string;
    switch (imageFormat) {
        case 'webp':
            mimeType = 'image/webp';
            fileExtension = 'webp';
            break;
        case 'jpeg':
            mimeType = 'image/jpeg';
            fileExtension = 'jpeg';
            break;
        case 'png':
        default:
            mimeType = 'image/png';
            fileExtension = 'png';
            break;
    }

    try {
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = imageData.width;
        outputCanvas.height = imageData.height;
        const outputCtx = outputCanvas.getContext('2d');
        if (!outputCtx) {
            throw new Error('Azure: Output canvas context not available');
        }
        outputCtx.putImageData(imageData, 0, 0);

        const normalizedQuality =
            typeof imageQuality === 'number' && Number.isFinite(imageQuality)
                ? Math.min(1, Math.max(0, imageQuality))
                : 1.0;

        const filename = `${Date.now()}_${Math.random().toString(36).substring(5)}.${fileExtension}`;
        const blob = await new Promise<Blob | null>((resolve) => {
            outputCanvas.toBlob(resolve, mimeType, normalizedQuality);
        });

        if (!blob) {
            throw new Error('Azure: Failed to convert canvas to Blob.');
        }

        return await uploadFileToCloud(blob, filename, mimeType, signal, ownerId);
    } catch (error) {
        console.error('Azure: Error in uploadImageDataToCloud:', error);
        return null;
    }
};
